import * as React from 'react'
import { X, Loader2, Trash2, Edit, Calendar, MapPin, User, Phone, Car, DollarSign, CheckCircle2 } from 'lucide-react'
import { createOrder } from '@/api/orders'
import type { AIParsedOrder } from '@/types'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { format } from 'date-fns'

interface BulkOrdersConfirmDrawerProps {
  isOpen: boolean
  onClose: () => void
  orders: AIParsedOrder[]
  onEditOrder?: (order: AIParsedOrder, index: number) => void
  onSuccess?: (createdOrderIds: string[]) => void
  rawText?: string
}

export default function BulkOrdersConfirmDrawer({
  isOpen,
  onClose,
  orders: initialOrders,
  onEditOrder,
  onSuccess,
  rawText,
}: BulkOrdersConfirmDrawerProps) {
  const [orders, setOrders] = React.useState<AIParsedOrder[]>(initialOrders)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [successCount, setSuccessCount] = React.useState(0)
  const [errors, setErrors] = React.useState<Record<number, string>>({})

  // Reset state when drawer opens with new orders
  React.useEffect(() => {
    if (isOpen) {
      setOrders(initialOrders)
      setSuccessCount(0)
      setErrors({})
    }
  }, [isOpen, initialOrders])

  const handleRemoveOrder = (index: number) => {
    setOrders(orders.filter((_, i) => i !== index))
    setErrors((prev) => {
      const newErrors = { ...prev }
      delete newErrors[index]
      return newErrors
    })
  }

  const handleEditOrder = (index: number) => {
    if (onEditOrder) {
      onEditOrder(orders[index], index)
    }
  }

  const handleBulkCreate = async () => {
    if (orders.length === 0) {
      onClose()
      return
    }

    setIsSubmitting(true)
    setErrors({})

    let success = 0
    const newErrors: Record<number, string> = {}
    const createdIds: string[] = []

    // Create orders one by one
    for (let i = 0; i < orders.length; i++) {
      try {
        const created = await createOrder(orders[i] as any)
        createdIds.push(created.id)
        success++
      } catch (err: any) {
        console.error(`Failed to create order ${i + 1}:`, err)
        newErrors[i] = err.response?.data?.detail || err.message || '建立失敗'
      }
    }

    setSuccessCount(success)
    setErrors(newErrors)

    // If all successful, close drawer
    if (success === orders.length) {
      setTimeout(() => {
        setIsSubmitting(false)
        onSuccess?.(createdIds)
        onClose()
      }, 500)
    } else {
      setIsSubmitting(false)
    }
  }

  const getStatusBadge = (index: number) => {
    if (errors[index]) {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
          失敗
        </span>
      )
    }
    if (successCount > 0 && !errors[index]) {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
          成功
        </span>
      )
    }
    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
        待建立
      </span>
    )
  }

  const getCategoryBadge = (category: string) => {
    const categoryMap: Record<string, string> = {
      '送機': 'text-blue-600',
      '接機': 'text-green-600',
      '其他': 'text-gray-600',
    }
    return (
      <span className={cn('font-medium text-xs', categoryMap[category] || 'text-gray-600')}>
        {category}
      </span>
    )
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 bg-black/40 z-40 transition-opacity',
          isSubmitting && 'pointer-events-none'
        )}
        onClick={isSubmitting ? undefined : onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full md:w-[600px] bg-white dark:bg-gray-800 shadow-xl z-50 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              批次建立訂單
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              共 {orders.length} 筆訂單待建立
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
              className="h-9 px-4 text-sm"
            >
              取消
            </Button>
            <Button
              onClick={handleBulkCreate}
              disabled={isSubmitting || orders.length === 0}
              className="h-9 px-6 bg-blue-600 hover:bg-blue-700 text-white font-bold"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  建立中...
                </>
              ) : (
                `批次建立 ${orders.length} 筆訂單`
              )}
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* AI 原始文字對照 */}
          {rawText && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-700 p-3">
              <div className="text-xs font-semibold text-blue-600 dark:text-blue-400 mb-1">AI 解析原文</div>
              <p className="text-xs text-blue-800 dark:text-blue-200 whitespace-pre-wrap leading-relaxed">{rawText}</p>
            </div>
          )}
          {orders.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full">
              <p className="text-gray-500 dark:text-gray-400">沒有訂單可建立</p>
              <CheckCircle2 className="w-12 h-12 text-gray-300 dark:text-gray-600" />
            </div>
          ) : (
            orders.map((order, index) => {
              const hasError = !!errors[index]
              const isSuccess = successCount > 0 && !hasError

              return (
                <div
                  key={index}
                  className={cn(
                    'p-4 rounded-lg border transition-all',
                    isSuccess && 'bg-green-50/50 dark:bg-green-900/10 border-green-200 dark:border-green-800',
                    hasError && 'bg-red-50/50 dark:bg-red-900/10 border-red-200 dark:border-red-800',
                    !isSuccess && !hasError && (
                    index % 2 === 0
                      ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                      : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                  )
                  )}
                >
                  {/* Order Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-gray-900 dark:text-white">
                        #{index + 1}
                      </span>
                      {getCategoryBadge(order.order_category)}
                      {getStatusBadge(index)}
                    </div>
                    {!isSubmitting && (
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditOrder(index)}
                          className="h-7 w-7 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                          title="編輯"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveOrder(index)}
                          className="h-7 w-7 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                          title="移除"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Error Message */}
                  {hasError && (
                    <div className="mb-3 p-2 bg-red-100 dark:bg-red-900/30 rounded text-xs text-red-700 dark:text-red-400">
                      {errors[index]}
                    </div>
                  )}

                  {/* Order Details */}
                  <div className="space-y-2 text-sm">
                    {/* Date & Time */}
                    <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="font-medium">
                        {order.pickup_datetime ? format(new Date(order.pickup_datetime), 'yyyy/MM/dd HH:mm') : '待確認'}
                      </span>
                    </div>

                    {/* Passenger */}
                    <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="font-medium">{order.passenger_name}</span>
                      <span className="text-gray-500">•</span>
                      {order.passenger_phone && <span className="text-gray-500">{order.passenger_phone}</span>}
                      <span className="text-gray-500">•</span>
                      <span>{order.passenger_count} 人</span>
                    </div>

                    {/* Route */}
                    <div className="space-y-1 bg-gray-50 dark:bg-gray-900/50 p-2 rounded">
                      <div className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5 shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300 text-sm">{order.pickup_location}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300 text-sm">{order.dropoff_location}</span>
                      </div>
                    </div>

                    {/* Additional Info Row */}
                    <div className="flex items-center gap-4 text-gray-600 dark:text-gray-400 text-xs">
                      <div className="flex items-center gap-1">
                        <Car className="w-3.5 h-3.5" />
                        <span>{order.vehicle_type}</span>
                      </div>
                      {order.luggage_count > 0 && (
                        <span>行李 {order.luggage_count} 件</span>
                      )}
                      {order.flight_number && (
                        <span>航班 {order.flight_number}</span>
                      )}
                      <div className="flex items-center gap-1">
                        <DollarSign className="w-3.5 h-3.5" />
                        <span className="font-medium">{Math.round(order.pricing.total_amount).toLocaleString()} 元</span>
                      </div>
                    </div>

                    {/* Special Options */}
                    <div className="flex gap-2 flex-wrap">
                      {order.is_round_trip && (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">來回</span>
                      )}
                      {order.child_seat && (
                        <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">兒童座椅</span>
                      )}
                      {order.name_board && (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">舉牌</span>
                      )}
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>
    </>
  )
}
